import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SearchForm from "../../components/SearchForm.jsx";
import { UserContext } from "../../contexts/UserContext.jsx";
import AddEditModal from "./Modals/AddEditModal.jsx";
import DeactivateModal from "./Modals/DeactivateModal.jsx";
import SellPointCard from "./SellPointCard.jsx";
import { getToken, URL } from "../../utils.js";
import ConditionalRender from "../../components/ConditionalRender";
import { usePermissions } from "../../hooks/usePermissions";
import { ROLES } from "../../constants.js";

const handleApiError = (error, setError) => {
  let errorMessage = "Une erreur s'est produite";

  if (error.response) {
    errorMessage = `Erreur ${error.response.status}: ${
      error.response.data.message || error.response.statusText
    }`;
  } else if (error.request) {
    errorMessage = "Erreur de réseau. Veuillez vérifier votre connexion.";
  } else {
    errorMessage = error.message;
  }

  setError(errorMessage);
  toast.error(errorMessage);
  return errorMessage;
};

const Entropots = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [data, setData] = useState([]);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const token = getToken();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Mémoïsation des données filtrées
  const filteredSellPoints = useMemo(() => {
    if (!searchTerm) return [];
    return data.filter(
      (item) =>
        item.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.quartier.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Récupération des données
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [response] = await Promise.all([
        axios.get(`${URL}Magasins/liste-entrepots`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (response.status === 200 && Array.isArray(response.data)) {
        setData(response.data);
        setError("");
      } else {
        setError("Les données reçues ne sont pas valides.");
      }
    } catch (err) {
      handleApiError(err, setError);
      console.error("Erreur lors de la récupération des données :", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Seuls les utilisateurs sans permission de voir tous les stores
    // ET qui sont responsables de boutique devraient être redirigés
    if (
      !hasPermission("canViewAllStores") ||
      user.role === ROLES.RESP_BOUTIQUE
    ) {
      // Vérifier que l'utilisateur a bien un magasin assigné avant de rediriger
      if (user.magasin) {
        navigate(`/stock/sell-point-details/${user.magasin}`);
      } else {
        // Si pas de magasin assigné, afficher une erreur
        navigate("/unauthorized");
      }
    } else {
      fetchData();
    }
  }, [fetchData, user, navigate]);

  // Gestion des modales
  const openModal = useCallback((type, point = null) => {
    setModalType(type);
    setCurrentPoint(
      point || {
        id: null,
        nom: "",
        quartier: "",
        country: "TG",
        capacite_stockage: "",
        type_entrepot: "principal",
        photo: null,
      }
    );
  }, []);

  const closeModal = useCallback(() => {
    setModalType(null);
    setCurrentPoint(null);
  }, []);

  // Recherche
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  return (
    <>
      <ToastContainer />
      <div className="shadow-md bg-white p-4 flex justify-between items-center sticky top-16 z-10">
        <SearchForm
          onSearch={handleSearch}
          placeholder="Rechercher un entrepôt"
        />
        <ConditionalRender requiredPermissions="canCreateWarehouse">
          <button
            onClick={() => openModal("add")}
            className="p-2 px-4 rounded-md bg-amber-500 text-white hover:bg-amber-600 focus:ring-2 border-2 border-amber-400 focus:ring-offset-2 focus:ring-amber-400 text-sm max-sm:hidden"
          >
            Ajouter un entrepôt
          </button>
        </ConditionalRender>
      </div>

      <div className="p-6 md:p-8 pt-6 space-y-4">
        <h1 className="font-medium text-2xl">Liste des entrepôts</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            Chargement en cours...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {searchTerm ? (
              filteredSellPoints.length > 0 ? (
                filteredSellPoints.map((item) => (
                  <MemoSellPointCard
                    key={item.id}
                    item={item}
                    onEdit={() => openModal("edit", item)}
                    onDeactivate={() => openModal("deactivate", item)}
                  />
                ))
              ) : (
                <NoResults />
              )
            ) : data.length > 0 ? (
              data.map((item) => (
                <MemoSellPointCard
                  key={item.id}
                  item={item}
                  onEdit={() => openModal("edit", item)}
                  onDeactivate={() => openModal("deactivate", item)}
                />
              ))
            ) : (
              <NoResults />
            )}
          </div>
        )}
      </div>

      {/* Modales */}
      {modalType === "add" || modalType === "edit" ? (
        <AddEditModal
          modalType={modalType}
          currentPoint={currentPoint}
          setCurrentPoint={setCurrentPoint}
          closeModal={closeModal}
          fetchData={fetchData}
          loading={loading}
          setLoading={setLoading}
          isWarehouse={true}
        />
      ) : modalType === "deactivate" ? (
        <DeactivateModal
          currentPoint={currentPoint}
          closeModal={closeModal}
          fetchData={fetchData}
          loading={loading}
          setLoading={setLoading}
        />
      ) : null}
    </>
  );
};

// Composants supplémentaires
const NoResults = () => (
  <div className="flex justify-center items-center h-64 text-gray-500 font-semibold col-span-full">
    Aucun Entrepôts trouvé
  </div>
);

const MemoSellPointCard = React.memo(SellPointCard);

export default Entropots;
